import { PUBLIC_MAGENTO_ENDPOINT } from '$env/static/public'
import { magentoAddToGuestCart, removeFromGuestCart, updateGuestCartItem } from '$lib/js/magento/cart/guest'
import { magentoAddToClientCart, removeFromClientCart, updateClientCartItem } from '$lib/js/magento/cart/client'
import { storeConfigurableChild, storeCustomerInfo, storeCustomerToken,storeCustomerTokenSet ,storeLoggedUser, storeUserCart } from '$lib/js/stores'
import { get } from 'svelte/store'
import { is_set_object } from '$lib/js/helpers'
import type { AddToCartItem, Cart, CartItem, CartItemInputInfo, MagentoCart } from '$lib/js/magento/cart/types'
import type { CustomerInfo, CustomerToken, CustomerTokenStatus } from '$lib/js/magento/types'
import { fetchJSON } from '$lib/js/helpers/http'
import { getCustomerInfo } from '../account/account'

/**
 * Get the current customer’s token.
 */
export async function getCustomerToken() {
	const token = await fetchJSON<CustomerToken>(`${PUBLIC_MAGENTO_ENDPOINT}/token/token/index`)
	if (token && token.masked_id === '') {
		token.masked_id = null
	}
	storeCustomerToken.set(token)
	storeCustomerTokenSet.set(true)
	return token
}

/**
 * Add an item to the cart.
 */
export async function magentoAddItemToCart(item: {
	sku: string
	type_id: string,
	attributes?: { attribute_id: number, attribute_value: string }[],
}, qty: number = 1) {
	// Get cart info
	return await getUserCart().then(async (cart_data) => {
		let added_item: CartItem,
			cart_item: { cartItem: AddToCartItem }

		const configurable_item = get(storeConfigurableChild)

		cart_item = {
			cartItem: {
				sku: item.type_id === 'configurable' ? configurable_item?.sku : item.sku,
				qty: qty,
				quote_id: cart_data.cart_id,
			},
		}

		if (cart_data.status === 'logged_in') {
			added_item = await magentoAddToClientCart(cart_item, cart_data)
		} else {
			added_item = await magentoAddToGuestCart(cart_item, cart_data)
		}

		return added_item
	})
}

/**
 * Update item in cart.
 */
export async function updateItemInCart(item: CartItemInputInfo) {
	// Get cart info
	let cart_data = await getUserCart()

	if (cart_data.token) {
		return await updateClientCartItem(item, cart_data)
	} else {
		return await updateGuestCartItem(item, cart_data)
	}
}

/**
 * Remove item from cart.
 */
export async function removeItemFromCart(item_id: number) {
	// Get cart info
	let cart_data = await getUserCart()

	if (cart_data.token) {
		await removeFromClientCart(item_id, cart_data)
	} else {
		await removeFromGuestCart(item_id, cart_data)
	}
}

/**
 * Get user cart
 */
export async function getUserCart() {
	const userCart = get(storeUserCart)

	if (is_set_object(userCart)) {
		return userCart
	}
	return getCart()
}

/**
 * Get cart data
 */
async function getCartData(token: CustomerToken | null) {
	if (token && token.status === 'logged_in') {
		let request = await getClientCart(token)

		if (request && request.status !== 200) {
			await initializeClientCart(token)
			request = await getClientCart(token)
		}

		const customer = await getCustomerInfo(token.token)
		storeCustomerInfo.set(customer)

		const cart = await request.json(),
			cart_data: Cart = {
				status: token.status,
				cart_id: cart.id,
				token: token.token,
				cart: cart,
			}
		storeUserCart.set(cart_data)
		storeLoggedUser.set(true)
		return cart_data

		// Get cart for guest
	} else if (token && token.status === 'guest' && token.masked_id) {
		const cart = await fetchJSON<MagentoCart>(
				`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/guest-carts/${token.masked_id}`,
				{
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				},
			),
			cart_data: Cart = {
				status: cart?.customer?.id ? 'logged_in' : <CustomerTokenStatus>token.status,
				cart_id: token.cart_id,
				masked_id: token.masked_id,
				cart: cart!,
			}

		storeUserCart.set(cart_data)
		storeLoggedUser.set(false)
		storeCustomerInfo.set({} as CustomerInfo)
		return cart_data
	} else {
		return undefined
	}
}

/**
 * Get cart
 */
export async function getCart() {
	let cart_data: Cart | undefined
	let token: CustomerToken | null = get(storeCustomerToken)

	// If is the first time we load and there's no token stored
	if (!is_set_object(token)) {
		token = await getCustomerToken()
	}

	cart_data = await getCartData(token)

	// It may happen that the session expired (the stored token is not valid anymore), so we try to get it again
	if (!cart_data) {
		token = await getCustomerToken()

		cart_data = await getCartData(token)

		// if it still breaks, clean token
		if (!cart_data) {
			storeCustomerToken.set({})
		}
	}

	return cart_data as Cart
}

/**
 * Get client cart
 */
async function getClientCart(token: CustomerToken) {
	return await fetch(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/carts/mine`, {
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			Authorization: 'Bearer ' + token.token,
		},
	})
}

/**
 * Initialize client cart
 */
async function initializeClientCart(token: CustomerToken) {
	await fetch(`${PUBLIC_MAGENTO_ENDPOINT}/rest/V1/carts/mine`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			Authorization: 'Bearer ' + token.token,
		},
	})
}
